import clsx from 'clsx';
import React, { useMemo } from 'react';
import {
  ApiFilterOperation,
  FilterComponentProps,
  ListSource,
  Select,
  TableFilterState,
  useEventCallback,
} from '@eas/common-web';
import { DomainObject } from '@models';
import { Operation, useSelectOperations } from '../filter-api';
import { useStyles } from '../filter-styles';

function buildSelectFilterState<TYPE extends DomainObject>(
  state: TableFilterState,
  operation: ApiFilterOperation,
  value: TYPE | string | TYPE[] | string[] | null
): TableFilterState {
  const selectedId = (value as TYPE)?.id;

  if (operation === ApiFilterOperation.NOT) {
    return {
      ...state,
      operation: ApiFilterOperation.NOT,
      value: selectedId,
      object: value,
      filters: selectedId
        ? [
            {
              field: state.field,
              operation: ApiFilterOperation.EQ,
              value: selectedId,
              object: value,
            },
          ]
        : undefined,
    };
  }

  const rest = { ...state };
  delete rest.filters;

  return {
    ...rest,
    operation: ApiFilterOperation.EQ,
    value: selectedId,
    object: value,
  };
}

export function FilterSelectCell<TYPE extends DomainObject>(
  dataHook: () => ListSource<TYPE>
) {
  return useMemo(
    () =>
      function Cell({ disabled, onChangeState, state }: FilterComponentProps) {
        const classes = useStyles();
        const source = dataHook();
        const operations = useSelectOperations();
        const operation =
          state.operation === ApiFilterOperation.NOT
            ? ApiFilterOperation.NOT
            : ApiFilterOperation.EQ;

        const handleChange = useEventCallback(
          (value: TYPE | string | TYPE[] | string[] | null) => {
            onChangeState(buildSelectFilterState(state, operation, value));
          }
        );

        const handleChangeOperation = useEventCallback(
          (value: Operation | Operation[] | string | string[] | null) => {
            if (!value) {
              return;
            }

            onChangeState(
              buildSelectFilterState(
                state,
                value as ApiFilterOperation,
                state.object ?? null
              )
            );
          }
        );

        return (
          <div className={clsx(classes.root, { [classes.disabled]: disabled })}>
            <div className={classes.selectWrapper}>
              <Select
                source={operations}
                value={operation}
                onChange={handleChangeOperation}
                valueIsId={true}
                clearable={false}
                disabled={disabled}
              />
            </div>
            <div className={classes.textWrapper}>
              <Select
                source={source}
                value={state.object}
                onChange={handleChange}
                disabled={disabled}
              />
            </div>
          </div>
        );
      },
    [dataHook]
  );
}
